import { useState, useRef, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { MapContainer, TileLayer, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const leafletMarkerIcon = L.icon({ 
	iconUrl: "/assets/leaflet-marker-icon.png", 
	iconRetinaUrl: "/assets/leaflet-marker-icon-2x.png",
	iconSize: [25, 41],
	iconAnchor: [12, 41],
	popupAnchor: [1, -34],
	tooltipAnchor: [16, -28],
	shadowUrl: "/assets/leaflet-marker-shadow.png",
	shadowSize: [41, 41],
	shadowAnchor: [12, 41],
})

interface LatLng {
	lat: number
	lng: number
}

interface LocationPickerProps {
	location: LatLng
	setLocation: (location: LatLng) => void
	className?: string
}

export function LocationPicker(props: LocationPickerProps) {
	const [isMapLoaded, setIsMapLoaded] = useState(false)
	const mapRef = useRef<L.Map | null>(null)
	const markerRef = useRef<L.Marker | null>(null)

	useEffect(
		() => {
			if (mapRef.current) {
				mapRef.current.invalidateSize()
			}
		}, 
		[isMapLoaded])

	const mapDidLoad = (): void => {
		setTimeout(
			() => {
				setIsMapLoaded(true)
				updateLocation(props.location)
			}, 
			0)
	}

	const didClickMap = (location: LatLng): void => {
		updateLocation(location)
	}

	const updateLocation = (location: LatLng): void => {
		props.setLocation(location)

		if (markerRef.current) {
			markerRef.current.setLatLng(location)
		} else {
			markerRef.current = L.marker(location, { icon: leafletMarkerIcon })
			if (mapRef.current) {
				markerRef.current.addTo(mapRef.current)
			}
		}
	}

	return (
		<div className={props.className}>
			<div className="h-64 relative">
				<MapContainer
					center={props.location}
					zoom={12}
					style={{ height: '100%', width: '100%', zIndex: 0 }}
					whenReady={mapDidLoad}
					ref={mapRef}
				>
					<TileLayer 
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" 
					/>
					<MapEvents didClickMap={didClickMap} />
				</MapContainer>
				{!isMapLoaded && (
					<div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 z-10">
						<Loader2 className="h-8 w-8 animate-spin text-primary" />
					</div>
				)}
			</div>
		</div>
	)
}

interface MapEventsProps {
	didClickMap: (location: LatLng) => void
}

function MapEvents(props: MapEventsProps) {
	useMapEvents({
		click(e: L.LeafletMouseEvent) {
			props.didClickMap(e.latlng)
		},
	})
	return null
}